import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Megaphone, Eye, EyeOff, Tag, RefreshCw, Calendar, ExternalLink,
  Instagram, Check, X, Clock, TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatDate } from "@/lib/dates";
import { errMsg } from "@/lib/errors";

export default function PromoSettingsTab() {
  const [data, setData] = useState(null);
  const [igData, setIgData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/promo-settings"),
        api.get("/admin/instagram-claims"),
      ]);
      setData(r1.data);
      setIgData(r2.data);
    } catch {
      toast.error("Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const togglePopup = async (val) => {
    setSaving(true);
    try {
      await api.put("/admin/promo-settings", { popup_enabled: val });
      setData((p) => ({ ...p, popup_enabled: val }));
      toast.success(val ? "Popup attivato" : "Popup disattivato");
    } catch { toast.error("Errore"); }
    finally { setSaving(false); }
  };

  const toggleLabel = async (label, enable) => {
    if (!data) return;
    const current = data.disabled_labels || [];
    const next = enable ? current.filter((l) => l !== label) : [...current, label];
    setSaving(true);
    try {
      await api.put("/admin/promo-settings", { disabled_labels: next });
      setData((p) => ({ ...p, disabled_labels: next }));
      toast.success(enable ? `"${label}" abilitata` : `"${label}" disabilitata`);
    } catch { toast.error("Errore"); }
    finally { setSaving(false); }
  };

  const verifyClaim = async (booking_id, verified) => {
    setSaving(true);
    try {
      await api.patch(`/admin/bookings/${booking_id}/instagram-verify`, { verified });
      toast.success(verified ? "Sconto Instagram confermato" : "Claim rifiutato — sconto rimosso");
      await load();
    } catch (err) {
      toast.error(errMsg(err, "Errore"));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data || !igData) return <div className="p-6 text-[#635F59]">Caricamento...</div>;

  const isPopupEnabled = data.popup_enabled !== false;
  const disabledSet = new Set(data.disabled_labels || []);
  const labels = data.active_labels || [];
  const stats = igData.stats || { total: 0, pending: 0, verified: 0, rejected: 0, confirmed_count: 0, discount_total: 0 };
  const pending = igData.pending || [];

  return (
    <section data-testid="promo-settings-tab" className="space-y-5">
      {/* Master switch */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${isPopupEnabled ? "bg-[#AC5D45]/15 text-[#AC5D45]" : "bg-[#E8E2D9] text-[#635F59]"}`}>
            <Megaphone className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-xl text-[#1E3A5F] mb-1">Popup promozionale in homepage</h3>
            <p className="text-sm text-[#635F59] mb-3">
              Quando attivo, i visitatori della home vedono un popup con le promozioni in corso.
              Disattivalo per nasconderlo del tutto, oppure escludi singole promozioni qui sotto.
            </p>
            <div className="flex items-center gap-3">
              <Switch checked={isPopupEnabled} onCheckedChange={togglePopup} disabled={saving} data-testid="popup-master-switch" />
              <span className={`text-sm font-semibold ${isPopupEnabled ? "text-emerald-700" : "text-[#635F59]"}`}>
                {isPopupEnabled ? <span className="inline-flex items-center gap-1.5"><Eye className="w-4 h-4" /> Popup ATTIVO</span> : <span className="inline-flex items-center gap-1.5"><EyeOff className="w-4 h-4" /> Popup DISATTIVO</span>}
              </span>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={saving} data-testid="promo-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna
          </Button>
        </div>
      </div>

      {/* Active promos list */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
              <Tag className="w-5 h-5" /> Promozioni in corso ({labels.length})
            </h3>
            <p className="text-xs text-[#635F59] mt-1">
              Ogni promozione corrisponde a una <code className="bg-[#FDFBF7] px-1 rounded">etichetta</code> sui giorni nel calendario prezzi.
            </p>
          </div>
          <Link to="/admin/channel-manager" className="text-xs font-semibold text-[#AC5D45] hover:underline inline-flex items-center gap-1">
            <ExternalLink className="w-3.5 h-3.5" /> Crea nuova → PMS
          </Link>
        </div>

        {labels.length === 0 ? (
          <div className="bg-[#FDFBF7] border border-dashed border-[#E8E2D9] rounded-lg p-8 text-center">
            <Megaphone className="w-10 h-10 text-[#E8E2D9] mx-auto mb-3" />
            <h4 className="font-display text-lg text-[#1E3A5F] mb-1">Nessuna promozione attiva</h4>
            <p className="text-sm text-[#635F59] mb-4">Crea promozioni dalla griglia PMS: seleziona giorni → "Modifica" → compila <em>Etichetta promo / Tipo / Sconto %</em>.</p>
            <Link to="/admin/channel-manager">
              <Button className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">Apri griglia PMS</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {labels.map((p) => {
              const isDisabled = disabledSet.has(p.label);
              return (
                <div key={p.label} data-testid={`promo-row-${p.label}`} className={`border rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3 transition ${isDisabled ? "border-[#E8E2D9] bg-[#FDFBF7] opacity-60" : "border-[#E8E2D9] bg-white"}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-semibold ${isDisabled ? "text-[#635F59]" : "text-[#1E3A5F]"}`}>{p.label}</span>
                      {p.discount_pct ? <span className="text-xs px-2 py-0.5 rounded-full bg-[#AC5D45] text-white font-semibold">−{Math.round(p.discount_pct)}%</span> : null}
                      {p.type ? <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#E8E2D9] text-[#635F59]">{p.type}</span> : null}
                    </div>
                    <div className="text-xs text-[#635F59] mt-1 flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(p.from_date)} → {formatDate(p.to_date)}</span>
                      <span>· {p.nights} {p.nights === 1 ? "notte" : "notti"} · {p.rooms_count} {p.rooms_count === 1 ? "camera" : "camere"}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-[#635F59]">{isDisabled ? "Esclusa" : "Mostrata"}</span>
                    <Switch checked={!isDisabled} onCheckedChange={(v) => toggleLabel(p.label, v)} disabled={saving} data-testid={`promo-toggle-${p.label}`} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Instagram claims dashboard */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-5" data-testid="ig-claims-block">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 bg-gradient-to-br from-[#E1306C] to-[#FFDC80] text-white">
            <Instagram className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-xl text-[#1E3A5F]">Sconti Instagram −5%</h3>
            <p className="text-xs text-[#635F59]">Verifica i claim, conferma o rifiuta. Lo sconto si applica solo dopo conferma admin.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5">
          <StatBox icon={TrendingUp} label="Totali" value={stats.total} testId="ig-stat-total" />
          <StatBox icon={Clock} label="Da verificare" value={stats.pending} accent="amber" testId="ig-stat-pending" />
          <StatBox icon={Check} label="Verificati" value={stats.verified} accent="emerald" testId="ig-stat-verified" />
          <StatBox icon={X} label="Rifiutati" value={stats.rejected} accent="rose" testId="ig-stat-rejected" />
          <StatBox label="Sconto erogato" value={`€${stats.discount_total.toFixed(2)}`} testId="ig-stat-discount" />
        </div>

        <h4 className="font-semibold text-[#1E3A5F] mb-2 text-sm">Da verificare ({pending.length})</h4>
        {pending.length === 0 ? (
          <div className="text-sm text-[#635F59] py-3">Nessun claim in attesa. Tutto in regola 👌</div>
        ) : (
          <div className="space-y-2">
            {pending.map((b) => (
              <div key={b.id} data-testid={`ig-pending-${b.id}`} className="border border-[#E8E2D9] rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono bg-[#FDFBF7] px-1.5 py-0.5 rounded border border-[#E8E2D9]">{b.code}</span>
                    <span className="font-semibold text-[#1E3A5F] text-sm truncate">{b.guest?.first_name} {b.guest?.last_name}</span>
                    <a
                      href={`https://instagram.com/${(b.instagram_handle || "").replace(/^@/, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`ig-handle-link-${b.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#E1306C] hover:underline font-semibold"
                    >
                      <Instagram className="w-3 h-3" /> @{(b.instagram_handle || "").replace(/^@/, "")}
                    </a>
                  </div>
                  <div className="text-xs text-[#635F59] mt-0.5">
                    {formatDate(b.check_in)} → {formatDate(b.check_out)} · €{(b.total || 0).toFixed(2)} · sconto da confermare €{(b.instagram_discount || 0).toFixed(2)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => verifyClaim(b.id, false)} disabled={saving} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`ig-reject-${b.id}`}>
                    <X className="w-4 h-4 mr-1" /> Rifiuta
                  </Button>
                  <Button size="sm" onClick={() => verifyClaim(b.id, true)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`ig-verify-${b.id}`}>
                    <Check className="w-4 h-4 mr-1" /> Verifica
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-[10px] text-[#9C9890] mt-3">
          💡 Tip: clicca sul @username per aprire il profilo Instagram e controllare se segue @bbemmanuel.
        </p>
      </div>
    </section>
  );
}

function StatBox({ icon: Icon, label, value, accent, testId }) {
  const accentMap = {
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
  };
  const cls = accent ? accentMap[accent] : "bg-[#FDFBF7] text-[#1E3A5F] border-[#E8E2D9]";
  return (
    <div data-testid={testId} className={`rounded-lg border p-2.5 ${cls}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-80">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="font-display text-xl font-semibold mt-0.5">{value}</div>
    </div>
  );
}
